import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { darkTheme, FONT } from '../../theme/tokens';
import { Setting, SettingsList } from '../connections/connectionUi';
import { ROLE_LABELS } from '../team/roleLabels';

const t = darkTheme;

// SC-3508 — the signed-in person's account and workspace, read-only. Built only
// from what `/auth/me` already returns: nothing here needs an endpoint, and there
// are no edit controls (no name/password change exists yet). How you sign in is
// left out on purpose — `/me` doesn't say whether the session came from SSO or a
// password. A Light/Dark switch waits on the components reading the active theme
// (they pin `darkTheme` today).
export function SettingsTab() {
  const { user, org, membership } = useAuth();
  // `membership` is `memberships[0]`, which for staff who also belong to an org
  // can be either membership depending on BE order (see AuthProvider). Show a role
  // only when it is the role in the org shown below (or there is no org), so an
  // EKOM Admin row never reads as a role in the client's workspace.
  const role =
    membership && (org ? membership.organization?.id === org.id : true)
      ? roleLabel(membership.role)
      : null;

  return (
    <div style={{ padding: '28px 32px', maxWidth: 820, fontFamily: FONT }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: t.text, margin: '0 0 4px' }}>Settings</h1>
      <p style={{ fontSize: 13, color: t.text3, margin: '0 0 24px' }}>
        Your account and workspace.
      </p>

      {user && (
        <Section title="Account">
          <SettingsList>
            <Setting label="Name">{user.name}</Setting>
            <Setting label="Email">{user.email}</Setting>
            {role && <Setting label="Role">{role}</Setting>}
            {memberSince(user.created_at) && (
              <Setting label="Member since">{memberSince(user.created_at)}</Setting>
            )}
          </SettingsList>
        </Section>
      )}

      {/* No organization (an EKOM staff account) → no workspace to describe. */}
      {org && (
        <Section title="Workspace">
          <SettingsList>
            <Setting label="Organization">{org.name}</Setting>
            <Setting label="Team" hint="People in your organization, and their roles.">
              <PageLink to="/team">View team</PageLink>
            </Setting>
            <Setting label="Connections" hint="Where your data comes from, and where it goes.">
              <PageLink to="/connections">View connections</PageLink>
            </Setting>
          </SettingsList>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: t.text, margin: '0 0 10px' }}>{title}</h2>
      {children}
    </section>
  );
}

function PageLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} style={{ color: t.blueText, textDecoration: 'none', fontWeight: 500 }}>
      {children} →
    </Link>
  );
}

// `Role` is a literal union, but the BE list can grow ahead of the FE type — fall
// back to the raw value rather than rendering nothing.
function roleLabel(role: string): string {
  return ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role;
}

/** "October 2026" from the account's `created_at`; null when unparseable. */
function memberSince(createdAt: string): string | null {
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
